// ** Modules
import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { useTheme } from '@mui/material/styles';

// ** Context
import { useHeaderOverride } from '@/context/HeaderOverrideContext';

// ** Components
import Link from '@/components/common/Link';
import ContentContainer from '@/components/common/ContentContainer';
import StrapiImageRenderer, { type StrapiImageRendererProps } from '@/components/strapi/StrapiImageRenderer';

// ** Types
import type { StrapiComponentProps } from '@/types/strapi';
import type { ComponentBlocksHero as BlocksHeroProps } from '@/types/graphql';

type InjectedHeroProps = {
	isMobile: boolean;
	segmentOverrides?: string[];
	newsBasePath?: string | null;
	eventsBasePath?: string | null;
	userBasePath?: string | null;
};
type ContentVariantProps = StrapiComponentProps<BlocksHeroProps> & InjectedHeroProps;

const HEADER_IMAGE_SX = {
	position: 'relative',
	width: '100%',
	height: { xs: '280px', sm: '400px', md: '500px' },
	overflow: 'hidden',
	background: 'black',
	'& .ContentHeader__heroImage': {
		objectFit: 'cover',
		objectPosition: { xs: '50% 20%', sm: '50% 25%', md: 'center' },
		opacity: 0.48,
		background: 'black'
	}
} as const;

const CONTENT_CONTAINER_SX = {
	pt: { xs: 8, md: 10 },
	pb: 0
} as const;

const ContentVariant = ({
	host,
	heroImage,
	headline,
	showMenu,
	segmentOverrides = [],
	newsBasePath,
	eventsBasePath,
	userBasePath
}: ContentVariantProps) => {
	const theme = useTheme();
	const { setOverride } = useHeaderOverride();

	const themeMode = theme.palette.mode;
	const router = useRouter();
	const pathSegments = router.asPath.split('/').filter((segment) => segment && !segment.includes('#'));

	// Translation
	const { t } = useTranslation('common');

	React.useEffect(() => {
		setOverride({
			appBarVariant: 'dark',
			showMenu: !!showMenu
		});

		return () => setOverride(null);
	}, [setOverride, showMenu]);

	const generateBreadcrumbs = () => {
		const breadcrumbs = [
			<Link key="home" href="/" passHref>
				<Typography
					variant="caption"
					sx={{
						textTransform: 'capitalize',
						color: theme.palette.text.tertiary ?? theme.palette.text.secondary ?? theme.palette.text.primary,
						opacity: 0.9,
						transition: 'opacity 0.2s ease, text-decoration-color 0.2s ease',
						textDecorationColor: 'transparent',
						'&:hover': {
							opacity: 1,
							textDecorationColor: 'rgba(255, 255, 255, 0.6)'
						}
					}}
				>
					{t('homepage')}
				</Typography>
			</Link>
		];

		let pathAccumulator = '';
		const offset = pathSegments.length - segmentOverrides.length;

		pathSegments.forEach((segment, index) => {
			pathAccumulator += `/${segment}`;

			const overrideValue = segmentOverrides[index - offset];
			const label = overrideValue ? overrideValue : segment.charAt(0).toUpperCase() + segment.slice(1);

			const isActive = index === pathSegments.length - 1;

			if (isActive) {
				breadcrumbs.push(
					<Typography
						variant="caption"
						key={pathAccumulator}
						sx={{
							textTransform: 'capitalize',
							color: theme.palette.text.tertiary ?? theme.palette.text.secondary ?? theme.palette.text.primary
						}}
					>
						{label}
					</Typography>
				);
			} else {
				let href = pathAccumulator;

				if (segment === 'news' && newsBasePath) {
					href = newsBasePath;
				} else if (segment === 'events' && eventsBasePath) {
					href = eventsBasePath;
				} else if (segment === 'user' && userBasePath) {
					href = userBasePath;
				}

				breadcrumbs.push(
					<Link key={pathAccumulator} href={href} passHref>
						<Typography
							variant="caption"
							sx={{
								textTransform: 'capitalize',
								color: theme.palette.text.tertiary ?? theme.palette.text.secondary ?? theme.palette.text.primary,
								opacity: 0.9,
								transition: 'opacity 0.2s ease, text-decoration-color 0.2s ease',
								textDecorationColor: 'transparent',
								'&:hover': {
									opacity: 1,
									textDecorationColor: 'rgba(255, 255, 255, 0.6)'
								}
							}}
						>
							{label}
						</Typography>
					</Link>
				);
			}
		});

		return breadcrumbs;
	};

	const isFullStrapiImage = !!(heroImage && (heroImage.__typename || heroImage.provider || heroImage.createdAt));

	const rendererProps: StrapiImageRendererProps = {
		host,
		themeMode,
		loading: 'eager',
		fill: true,
		className: 'ContentHeader__heroImage',
		sizes: '(max-width: 600px) 100vw, (max-width: 900px) 100vw, 100vw',
		alt: heroImage?.alternativeText || headline || 'Hero Image'
	};

	if (isFullStrapiImage) {
		rendererProps.imageData = heroImage;
	} else {
		rendererProps.src = typeof heroImage === 'string' ? heroImage : heroImage?.url;
	}

	return (
		<Box
			sx={{
				position: 'relative',
				height: '100%'
			}}
		>
			<Box sx={HEADER_IMAGE_SX}>
				<StrapiImageRenderer {...rendererProps} />
			</Box>

			<ContentContainer disableGutters sx={(t) => ({ ...CONTENT_CONTAINER_SX, px: t.layout.pagePx })}>
				<Breadcrumbs separator=">" aria-label="breadcrumb">
					{generateBreadcrumbs()}
				</Breadcrumbs>

				<Typography variant="title-lg" sx={{ mt: { xs: 8, md: 16 }, color: theme.palette.common.black }}>
					{headline}
				</Typography>
			</ContentContainer>
		</Box>
	);
};

export default ContentVariant;
